import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	contentChild,
	effect,
	ElementRef,
	inject,
	linkedSignal,
	type TemplateRef,
	untracked,
	viewChild,
} from '@angular/core';

import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import type { EffIconName } from '@efficiently/client-ui-icons';
import { EffIconButtonComponent } from '@efficiently/client-ui-kit';

import { DomainNavigatorStore } from './domain-navigator.store';

const PANEL_ID = 'ds-domain-navigator-panel';
const TOGGLE_LABEL = 'Domain navigator';
const PANEL_LABEL = 'Domains';
const OPEN_TOGGLE_ICON: EffIconName = 'chevron-left';
const CLOSED_TOGGLE_ICON: EffIconName = 'chevron-right';

@Component({
	selector: 'ds-domain-navigator',
	imports: [EffIconButtonComponent, NgComponentOutlet, NgTemplateOutlet, TestIdDirective],
	templateUrl: './domain-navigator.component.html',
	styleUrl: './domain-navigator.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'(document:pointerdown)': 'closeOnOutsidePointer($event)',
		'(document:keydown.escape)': 'closeOnEscape($event)',
	},
})
export class DomainNavigatorComponent {
	private readonly store = inject(DomainNavigatorStore);
	private readonly overlayContainer = inject(OverlayContainer);
	private readonly document = inject(DOCUMENT);
	private readonly toggleButton = viewChild<string, ElementRef<HTMLElement>>('toggleButton', { read: ElementRef });
	private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

	private isClosedByOutsidePointer = false;

	protected readonly panelId = PANEL_ID;
	protected readonly toggleLabel = TOGGLE_LABEL;
	protected readonly panelLabel = PANEL_LABEL;
	protected readonly content = this.store.content;
	/** The pinned footer, projected as `<ng-template #footer>`; stamped on the first open, like the content. */
	protected readonly footer = contentChild<TemplateRef<unknown>>('footer');
	protected readonly isOpen = this.store.isOpen;
	protected readonly toggleIcon = computed<EffIconName>(() =>
		this.isOpen() ? OPEN_TOGGLE_ICON : CLOSED_TOGGLE_ICON,
	);

	protected readonly hasOpened = linkedSignal<boolean, boolean>({
		source: this.isOpen,
		computation: (isOpen, previous) => isOpen || (previous?.value ?? false),
	});

	constructor() {
		effect(() => {
			if (this.isOpen()) {
				return;
			}

			untracked(() => this.returnFocusFromPanel());
		});
	}

	protected toggle(): void {
		this.store.toggle();
	}

	protected closeOnOutsidePointer(event: PointerEvent): void {
		if (!this.isOpen() || this.isInsideNavigator(event.target)) {
			return;
		}

		this.isClosedByOutsidePointer = true;
		this.store.close();
	}

	protected closeOnEscape(event: KeyboardEvent): void {
		if (!this.isOpen() || event.defaultPrevented || this.isInsideOverlay(event.target)) {
			return;
		}

		this.store.close();
	}

	private returnFocusFromPanel(): void {
		const isClosedByOutsidePointer = this.isClosedByOutsidePointer;
		this.isClosedByOutsidePointer = false;

		const panel = this.panel()?.nativeElement;

		if (isClosedByOutsidePointer || panel === undefined || !panel.contains(this.document.activeElement)) {
			return;
		}

		this.toggleButton()?.nativeElement.focus();
	}

	private isInsideNavigator(target: EventTarget | null): boolean {
		if (!(target instanceof Node)) {
			return false;
		}

		return (
			this.panel()?.nativeElement.contains(target) === true ||
			this.toggleButton()?.nativeElement.contains(target) === true ||
			this.isInsideOverlay(target)
		);
	}

	private isInsideOverlay(target: EventTarget | null): boolean {
		return target instanceof Node && this.overlayContainer.getContainerElement().contains(target);
	}
}
